import { useState, useEffect, useRef } from 'react';
import { getWatchlistQuotes } from '../../utils/api';
import { fmt, colorByValue } from '../../utils/format';
import useLang from '../../hooks/useLang';
import { t } from '../../utils/i18n';

const SECTOR_COLORS = {
  'Technology':             '#3b82f6',
  'Communication Services': '#8b5cf6',
  'Consumer Cyclical':      '#f97316',
  'Consumer Defensive':     '#22c55e',
  'Healthcare':             '#ec4899',
  'Financials':             '#eab308',
  'Financial Services':     '#eab308',
  'Industrials':            '#64748b',
  'Energy':                 '#ef4444',
  'Materials':              '#84cc16',
  'Real Estate':            '#06b6d4',
  'Utilities':              '#a78bfa',
  'ETF':                    '#06b6d4',
};

const sectorColor = (sector) => SECTOR_COLORS[sector] || '#6b7280';

const SECTOR_ORDER_KEY = 'freejini_sector_order';
const getSectorOrder = () => JSON.parse(localStorage.getItem(SECTOR_ORDER_KEY) || '[]');
const saveSectorOrder = (order) => localStorage.setItem(SECTOR_ORDER_KEY, JSON.stringify(order));

export default function Watchlist({ onSelect, currentSymbol, alertCounts = {}, watchlist }) {
  const { lang } = useLang();
  const symbols = watchlist?.symbols || [];
  const [quotes, setQuotes] = useState({});
  const [sectorOrder, setSectorOrder] = useState(getSectorOrder());

  // drag refs — type: 'item' | 'sector'
  const dragType = useRef(null);
  const dragItem = useRef(null);   // symbol string
  const dragSector = useRef(null); // sector string
  const overItem = useRef(null);
  const overSector = useRef(null);

  const fetchQuotes = () => {
    if (symbols.length === 0) { setQuotes({}); return; }
    getWatchlistQuotes(symbols).then(data => {
      const map = {};
      data.forEach(q => { if (q) map[q.symbol] = q; });
      setQuotes(map);
    }).catch(() => {});
  };

  useEffect(() => {
    fetchQuotes();
    const id = setInterval(fetchQuotes, 30000);
    return () => clearInterval(id);
  }, [symbols]);

  const remove = (e, sym) => {
    e.stopPropagation();
    watchlist?.remove(sym);
  };

  // ── Item drag ──
  const onItemDragStart = (e, sym) => {
    dragType.current = 'item';
    dragItem.current = sym;
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.4';
  };
  const onItemDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    dragItem.current = null;
    overItem.current = null;
  };
  const onItemDrop = (e, targetSym) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragType.current !== 'item') return;
    const from = dragItem.current;
    if (!from || from === targetSym) return;
    const next = [...symbols];
    const fromIdx = next.indexOf(from);
    const toIdx = next.indexOf(targetSym);
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, from);
    watchlist?.reorder(next);
  };

  // ── Sector drag ──
  const onSectorDragStart = (e, sector) => {
    dragType.current = 'sector';
    dragSector.current = sector;
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
    e.currentTarget.closest('[data-sector]').style.opacity = '0.4';
  };
  const onSectorDragEnd = (e) => {
    const el = e.currentTarget.closest('[data-sector]');
    if (el) el.style.opacity = '1';
    dragSector.current = null;
    overSector.current = null;
  };
  const onSectorDragEnter = (e, sector) => {
    if (dragType.current !== 'sector') return;
    overSector.current = sector;
  };
  const onSectorDrop = (e, targetSector) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragType.current !== 'sector') return;
    const from = dragSector.current;
    if (!from || from === targetSector) return;

    // Reorder symbols: move all `from` sector symbols as a block to where `targetSector` starts
    const grouped = symbols.reduce((acc, sym) => {
      const sector = quotes[sym]?.sector || 'Other';
      if (!acc[sector]) acc[sector] = [];
      acc[sector].push(sym);
      return acc;
    }, {});

    // Build new sorted sector list
    const currentOrder = buildSectorOrder(Object.keys(grouped), sectorOrder);
    const fromIdx = currentOrder.indexOf(from);
    const toIdx = currentOrder.indexOf(targetSector);
    const newOrder = [...currentOrder];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, from);

    saveSectorOrder(newOrder);
    setSectorOrder(newOrder);

    // Also reorder the symbols array to match the new sector order
    const newSymbols = newOrder.flatMap(s => grouped[s] || []);
    watchlist?.reorder(newSymbols);
  };

  // Group symbols by sector preserving order
  const grouped = symbols.reduce((acc, sym) => {
    const sector = quotes[sym]?.sector || 'Other';
    if (!acc[sector]) acc[sector] = [];
    acc[sector].push(sym);
    return acc;
  }, {});

  const sortedSectors = buildSectorOrder(Object.keys(grouped), sectorOrder);

  if (symbols.length === 0) {
    return (
      <div className="watchlist-section">
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', padding: '8px 0 12px', fontWeight: 600 }}>{t('watchlist', lang)}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>{t('noStocks', lang)}</div>
      </div>
    );
  }

  return (
    <div className="watchlist-section">
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', padding: '8px 0 12px', fontWeight: 600 }}>
        {t('watchlist', lang)} <span style={{ color: 'var(--accent)' }}>({symbols.length})</span>
      </div>

      {sortedSectors.map(sector => (
        <div
          key={sector}
          data-sector={sector}
          style={{ marginBottom: 12, transition: 'opacity 0.15s' }}
          onDragEnter={(e) => onSectorDragEnter(e, sector)}
          onDragOver={(e) => { if (dragType.current === 'sector') e.preventDefault(); }}
          onDrop={(e) => onSectorDrop(e, sector)}
        >
          {/* Sector Header — draggable */}
          <div
            draggable
            onDragStart={(e) => onSectorDragStart(e, sector)}
            onDragEnd={onSectorDragEnd}
            style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, padding: '4px 2px', cursor: 'grab', borderRadius: 4 }}
          >
            <div style={{ color: 'var(--text-muted)', fontSize: 10, cursor: 'grab', lineHeight: 1 }}>⠿</div>
            <div style={{ width: 3, height: 12, borderRadius: 2, background: sectorColor(sector), flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: sectorColor(sector) }}>
              {sector}
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>({grouped[sector].length})</span>
          </div>

          {/* Stocks in this sector */}
          {grouped[sector].map(sym => {
            const q = quotes[sym];
            const isActive = sym === currentSymbol;
            return (
              <div
                key={sym}
                className="watchlist-item"
                draggable
                onDragStart={(e) => onItemDragStart(e, sym)}
                onDragEnd={onItemDragEnd}
                onDragEnter={(e) => { if (dragType.current === 'item') overItem.current = sym; }}
                onDragOver={(e) => { if (dragType.current === 'item') e.preventDefault(); }}
                onDrop={(e) => onItemDrop(e, sym)}
                style={{
                  background: isActive ? 'var(--accent-dim)' : undefined,
                  borderLeft: `2px solid ${isActive ? 'var(--accent)' : 'transparent'}`,
                  paddingLeft: 10,
                  marginLeft: 4,
                  cursor: 'grab',
                  transition: 'opacity 0.15s',
                }}
                onClick={() => onSelect(sym)}
              >
                <div style={{ color: 'var(--text-muted)', fontSize: 11, marginRight: 4, flexShrink: 0, lineHeight: 1 }}>⠿</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, fontSize: 12 }}>{sym}</div>
                    {alertCounts[sym] > 0 && (
                      <span title={`${alertCounts[sym]} active alert${alertCounts[sym] > 1 ? 's' : ''}`} style={{
                        fontSize: 9, fontWeight: 700,
                        background: 'var(--accent)', color: '#fff',
                        borderRadius: 10, padding: '1px 5px', lineHeight: '14px',
                        flexShrink: 0,
                      }}>🔔{alertCounts[sym]}</span>
                    )}
                  </div>
                  {q?.name && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100 }}>
                      {q.name}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                  {q?.price && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 12, fontWeight: 500 }}>{fmt.currency(q.price)}</div>}
                  {q?.changePercent != null && (
                    <div style={{ fontSize: 10, color: colorByValue(q.changePercent) }}>{fmt.change(q.changePercent)}</div>
                  )}
                  {/* Pre / Post market */}
                  {(() => {
                    const state = q?.marketState;
                    const extPrice = state === 'PRE' ? q?.preMarketPrice : (state === 'POST' || state === 'POSTPOST') ? q?.postMarketPrice : null;
                    const extPct   = state === 'PRE' ? q?.preMarketChangePercent : q?.postMarketChangePercent;
                    const label    = state === 'PRE' ? 'Pre' : 'Post';
                    if (!extPrice) return null;
                    return (
                      <div style={{ fontSize: 9, color: 'var(--text-muted)', textAlign: 'right', lineHeight: 1.4 }}>
                        <span style={{ marginRight: 3 }}>{label}</span>
                        <span style={{ fontFamily: 'JetBrains Mono', color: 'var(--text-secondary)' }}>{fmt.currency(extPrice)}</span>
                        {extPct != null && (
                          <span style={{ color: colorByValue(extPct), marginLeft: 3 }}>{fmt.change(extPct)}</span>
                        )}
                      </div>
                    );
                  })()}
                  <button
                    onClick={(e) => remove(e, sym)}
                    style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1, marginTop: 1 }}
                    title="Remove"
                  >×</button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Merge saved order with current sectors (new sectors appended at end)
function buildSectorOrder(currentSectors, savedOrder) {
  const inSaved = savedOrder.filter(s => currentSectors.includes(s));
  const notInSaved = currentSectors.filter(s => !savedOrder.includes(s));
  return [...inSaved, ...notInSaved];
}
